import { ImagesSubComponent } from '../images.sub.component';
import { getCurrentMajorBreakpoint, MajorBreakpoint } from '../../../_common/client/breakpoint';
import { FALLBACK_IMAGE_ID, getImageUrl } from '../../../_common/client/images';

export class AlternateImagesSubComponent extends ImagesSubComponent {
    public rootSelector = '.js_pdp_alternate-images';

    private viewportHeight = 0;
    private viewportTop = 0;
    private viewportBottom = 0;
    private thumbnailListTop = 0;
    private thumbnailListHeight = 0;

    public async onHtmlLoaded(): Promise<void> {
        if (!this.isPresent()) {
            return;
        }

        if (getCurrentMajorBreakpoint() === MajorBreakpoint.Desktop) {
            await this.loadImages();
            this.moveThumbnailSelection(0);
            this.showOrHideButtons();
            this.setEventEnterHandlers();
        }
    }

    protected onItemChanged(index: number): void {
        if (!this.isPresent()) return;

        if (this.isVideo(index)) {
            this.deselectAllThumbnails();
            return;
        }

        this.goToThumbnail(this.toImageIndex(index));
    }

    private async loadImages(): Promise<void> {
        const thumbnailElements = this.getThumbnailElements();
        await Promise.all(
            thumbnailElements.map((thumbnailElement, idx) => {
                return new Promise<void>(resolve => {
                    const imageId = thumbnailElement.dataset.imageId || FALLBACK_IMAGE_ID;
                    const image = new Image();
                    image.onload = () => resolve();
                    image.onerror = () => {
                        image.src = image.src.replace(imageId, FALLBACK_IMAGE_ID);
                        image.onerror = () => {
                            resolve();
                        };
                    };
                    image.src = getImageUrl(imageId, '$001PICT36$&fmt=auto');
                    image.alt = `Detailansicht des Produkts ${idx}`;
                    thumbnailElement.appendChild(image);
                });
            })
        );
    }

    private getViewportElement(): HTMLElement {
        return this.getChildElement('.js_pdp_alternate-images__viewport');
    }

    private getThumbnailListElement(): HTMLElement {
        return this.getChildElement('.js_pdp_alternate-images__thumbnail-list');
    }

    private getThumbnailElements(): HTMLElement[] {
        return this.getChildElements('.js_pdp_alternate-images__thumbnail');
    }

    private getBtnUpElement(): HTMLButtonElement {
        return this.getChildElement('.js_pdp_alternate-images__btn--up');
    }

    private getBtnDownElement(): HTMLButtonElement {
        return this.getChildElement('.js_pdp_alternate-images__btn--down');
    }

    private moveThumbnailSelection(newIndex: number): void {
        this.getThumbnailElements().forEach((thumbnail, index) => {
            if (index === newIndex) {
                thumbnail.classList.add('pdp_alternate-images__thumbnail--selected');
            } else {
                thumbnail.classList.remove('pdp_alternate-images__thumbnail--selected');
            }
        });
    }

    private deselectAllThumbnails(): void {
        this.getThumbnailElements().forEach(thumbnailElement => {
            thumbnailElement.classList.remove('pdp_alternate-images__thumbnail--selected');
        });
    }

    private moveThumbnailListTo(newAbsoluteViewportTop: number): void {
        let newRelativeViewportTop = newAbsoluteViewportTop - this.thumbnailListTop;
        if (newRelativeViewportTop <= 0) {
            newRelativeViewportTop = 0;
            this.getBtnUpElement().disabled = true;
            this.getBtnDownElement().disabled = false;
        } else if (newRelativeViewportTop > this.thumbnailListHeight - this.viewportHeight) {
            newRelativeViewportTop = this.thumbnailListHeight - this.viewportHeight;
            this.getBtnUpElement().disabled = false;
            this.getBtnDownElement().disabled = true;
        } else {
            this.getBtnUpElement().disabled = false;
            this.getBtnDownElement().disabled = false;
        }

        const newThumbnailListTop = 0 - newRelativeViewportTop;

        this.getThumbnailListElement().style.top = newThumbnailListTop + 'px';
    }

    private static getBoundingClientRect(el: HTMLElement): DOMRect {
        if (el && typeof el.getBoundingClientRect === 'function') {
            return el.getBoundingClientRect();
        } else {
            return new DOMRect(0, 0, 0, 0);
        }
    }

    private refreshSavedElementLocations(): void {
        const viewportBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(this.getViewportElement());
        const thumbnailListBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(this.getThumbnailListElement());

        /*                                                                                           */
        this.viewportHeight = viewportBoundingClientRect.bottom - viewportBoundingClientRect.top;
        this.viewportTop = viewportBoundingClientRect.top;
        this.viewportBottom = viewportBoundingClientRect.bottom;
        this.thumbnailListTop = thumbnailListBoundingClientRect.top;
        this.thumbnailListHeight = thumbnailListBoundingClientRect.bottom - thumbnailListBoundingClientRect.top;
    }

    private goToThumbnail(index: number): void {
        const thumbnailElements = this.getThumbnailElements();
        if (thumbnailElements.length > 1) {
            if (!thumbnailElements[index]) {
                throw 'going to thumbnail of index: ' + index + ' is not possible. ThumbnailElements length is ' + thumbnailElements.length;
            }

            this.refreshSavedElementLocations();

            const thumbnailBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(thumbnailElements[index]);
            this.moveThumbnailListTo(thumbnailBoundingClientRect.top);

            this.moveThumbnailSelection(index);
        }
    }

    private showOrHideButtons(): void {
        this.refreshSavedElementLocations();

        if (Math.floor(this.thumbnailListHeight) > Math.floor(this.viewportHeight)) {
            this.getBtnUpElement().classList.remove('oc-visually-hidden');
            this.getBtnDownElement().classList.remove('oc-visually-hidden');
        }
    }

    private trackButtonClick(direction: string): void {
        this.trackEvent({
            product_DifferentImageScroll: direction,
            product_DifferentImageMethod: 'button',
        });
    }

    /*                                                                                                        */
    /*                                                                                           */
    private getNewAbsoluteViewportTopMoveDown(): number {
        const thumbnailElements = this.getThumbnailElements();
        for (let i = 0; i < thumbnailElements.length; i++) {
            const thumbnailBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(thumbnailElements[i]);
            if (thumbnailBoundingClientRect.bottom > this.viewportBottom) {
                return thumbnailBoundingClientRect.top;
            }
        }
        return 0;
    }

    /*                                                                                                               */
    /*                                                                                                         */
    /*        */
    private getNewAbsoluteViewportTopMoveUp(): number {
        const itemElements = this.getThumbnailElements();
        if (itemElements.length > 1) {
            let previousThumbnailBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(itemElements[0]);
            for (let i = 1; i < itemElements.length; i++) {
                const thumbnailBoundingClientRect = AlternateImagesSubComponent.getBoundingClientRect(itemElements[i]);
                if (thumbnailBoundingClientRect.top >= this.viewportTop) {
                    return previousThumbnailBoundingClientRect.bottom - this.viewportHeight;
                }
                previousThumbnailBoundingClientRect = thumbnailBoundingClientRect;
            }
        }
        return 0;
    }

    private handleMouseEnterEvent(thumbnailElement: HTMLElement, index: number): void {
        if (!thumbnailElement.classList.contains('pdp_alternate-images__thumbnail--selected')) {
            this.moveThumbnailSelection(index);
            this.changeItem(this.toFullIndex(index));
        }
    }

    private handleUpButtonClickEvent(): void {
        this.refreshSavedElementLocations();
        this.moveThumbnailListTo(this.getNewAbsoluteViewportTopMoveUp());
        this.trackButtonClick('up');
    }

    private handleDownButtonClickEvent(): void {
        this.refreshSavedElementLocations();
        this.moveThumbnailListTo(this.getNewAbsoluteViewportTopMoveDown());
        this.trackButtonClick('down');
    }

    private setEventEnterHandlers(): void {
        const thumbnailElements = this.getThumbnailElements();
        thumbnailElements.forEach((thumbnailElement, index) => {
            thumbnailElement.addEventListener('click', () => this.handleMouseEnterEvent(thumbnailElement, index));
            thumbnailElement.addEventListener('mouseenter', () => this.handleMouseEnterEvent(thumbnailElement, index));
        });
        this.getBtnUpElement().addEventListener('click', () => this.handleUpButtonClickEvent());
        this.getBtnDownElement().addEventListener('click', () => this.handleDownButtonClickEvent());
    }
}
